import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Eye, EyeOff, LocateFixed, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { focusOnMap, locateMe } from "@/lib/mapFocus";
import { Switch } from "@/components/ui/switch";
import { Avatar } from "@/components/friends/Avatar";
import { DockSheet } from "./DockSheet";

const ago = (iso) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : "");

const useSettings = () => {
  const qc = useQueryClient();
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const save = async (b) => {
    qc.setQueryData(["settings"], (x) => ({ ...x, ...b }));
    await api.saveSettings(b).catch(() => toast.error("Couldn't save"));
    qc.invalidateQueries({ queryKey: ["settings"] });
  };
  return { s, save, qc };
};

const MyCity = () => {
  const { s, qc } = useSettings();
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const loc = await api.setLocation(await locateMe(true));
      toast.success(`Sharing your city: ${loc.city}`);
    } catch (e) { toast.error(e.message || "Couldn't share your city"); }
    qc.invalidateQueries({ queryKey: ["settings"] });
    setBusy(false);
  };
  const stop = async () => { await api.stopLocation(); toast.success("Stopped sharing your city"); qc.invalidateQueries({ queryKey: ["settings"] }); };
  const on = !!s?.location_share;
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="locate-my-city">
      <label className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium flex items-center gap-2"><LocateFixed size={15} className="text-emerald-300" /> Share my city with friends</span>
        <Switch checked={on} disabled={busy} onCheckedChange={(v) => (v ? share() : stop())} data-testid="locate-share-toggle" />
      </label>
      <p className="text-xs text-slate-400 mt-2" data-testid="locate-my-status">
        {on && s.location ? <>Friends see you in <b className="text-slate-200">{s.location.city}, {s.location.country}</b> · updated {ago(s.location.at)}</> : "Only the city you're in is shared — never your exact location."}
      </p>
      {on && <button onClick={share} disabled={busy} className="mt-2 flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200" data-testid="locate-refresh"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Update my city now</button>}
    </section>
  );
};

const WhoSees = () => {
  const { s, save } = useSettings();
  const { data: friends = [] } = useQuery({ queryKey: ["friends"], queryFn: api.friends });
  const mutual = friends.filter((f) => f.mutual);
  if (!s?.location_share || !mutual.length) return null;
  const blocked = s.location_blocked || [];
  const toggle = (id, v) => save({ location_blocked: v ? blocked.filter((x) => x !== id) : [...blocked, id] });
  return (
    <section data-testid="locate-who-sees">
      <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-1">Who can see my city</h3>
      <ul>
        {mutual.map((f) => (
          <li key={f.friend_id} className="flex items-center gap-3 py-2 border-b border-white/5">
            <Avatar f={f} /> <span className="flex-1 text-sm truncate">{f.name}</span>
            <Switch checked={!blocked.includes(f.friend_id)} onCheckedChange={(v) => toggle(f.friend_id, v)} data-testid={`locate-allow-${f.friend_id}`} />
          </li>
        ))}
      </ul>
    </section>
  );
};

const FriendCities = ({ close }) => {
  const { s, save } = useSettings();
  const { data: pins = [] } = useQuery({ queryKey: ["friend-locations"], queryFn: api.friendLocations, refetchInterval: 120000 });
  const hidden = s?.map_hidden || [];
  const toggle = (id) => save({ map_hidden: hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id] });
  return (
    <section data-testid="locate-friend-cities">
      <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-1">Friends on my map</h3>
      <ul>
        {pins.map((p) => {
          const off = hidden.includes(p.friend_id);
          return (
            <li key={p.friend_id} className={`flex items-center gap-3 py-2 border-b border-white/5 ${off ? "opacity-50" : ""}`} data-testid={`locate-friend-${p.friend_id}`}>
              <Avatar f={p} />
              <button onClick={() => { close(); setTimeout(() => focusOnMap(p.country, [p.city], false), 250); }} className="flex-1 min-w-0 text-left" data-testid={`locate-friend-focus-${p.friend_id}`}>
                <p className="text-sm truncate">{p.name}</p>
                <p className="text-xs text-slate-500 truncate">{p.city}, {p.country} · {ago(p.at)}</p>
              </button>
              <button onClick={() => toggle(p.friend_id)} aria-label={off ? `Show ${p.name} on map` : `Hide ${p.name} from map`}
                className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${off ? "border-white/10 text-slate-500" : "border-sky-400/40 text-sky-300"}`} data-testid={`locate-friend-toggle-${p.friend_id}`}>
                {off ? <><EyeOff size={12} /> Hidden</> : <><Eye size={12} /> On map</>}
              </button>
            </li>
          );
        })}
        {!pins.length && <li className="py-6 text-center text-sm text-slate-500" data-testid="locate-friends-empty">None of your friends are sharing their city right now.</li>}
      </ul>
    </section>
  );
};

export const LocateSheet = ({ open, onOpenChange }) => (
  <DockSheet open={open} onOpenChange={onOpenChange} title="Locate friends" description="See which city your friends are in. Only cities are shared, never exact locations." testId="locate-sheet">
    <div className="mt-4 flex-1 overflow-y-auto space-y-6 pr-1">
      <MyCity />
      <FriendCities close={() => onOpenChange(false)} />
      <WhoSees />
    </div>
  </DockSheet>
);
